/* Created: confirmation dialog for an admin order status change (including cancellation).
   - Three explicit choices: "Annuleren" (nothing changes), "Alleen status wijzigen" (no customer mail) and
     "Status wijzigen en klant mailen" (primary).
   - Shows beforehand which customer mail will be sent, or why none exists for this status.
   - No button is focused on open and clicks in the first moments are ignored, so a click or Enter that
     opened the dialog can never pick an option by accident.
   - Buttons are stacked full-width so they always fit the dialog; "Annuleren" is red (destructive).
   - Choices stay disabled until the mail check is done; if that check fails, both choices are offered. */

import { useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { MAIL_TYPE_LABELS, previewStatusMail, type StatusMailPlan } from '@/lib/transactional-mail';
import { Loader as Loader2, Mail, MailX } from 'lucide-react';

const ARM_DELAY_MS = 600;

interface StatusChangeDialogProps {
  order: { id: string; order_number: string; customer_email: string } | null;
  newStatus: string;
  statusLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (sendEmail: boolean) => void;
}

export default function StatusChangeDialog({ order, newStatus, statusLabel, busy, onCancel, onConfirm }: StatusChangeDialogProps) {
  const [plan, setPlan] = useState<StatusMailPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const openedAt = useRef(0);

  useEffect(() => {
    if (!order) return;
    let active = true;
    setPlan(null);
    setPlanError(null);
    setArmed(false);
    openedAt.current = Date.now();
    const timer = window.setTimeout(() => active && setArmed(true), ARM_DELAY_MS);
    previewStatusMail(order.id, newStatus)
      .then((result) => active && setPlan(result))
      .catch((error) => active && setPlanError(error instanceof Error ? error.message : 'Onbekende fout'));
    return () => { active = false; window.clearTimeout(timer); };
  }, [order, newStatus]);

  const choose = (sendEmail: boolean) => {
    if (busy || Date.now() - openedAt.current < ARM_DELAY_MS) return;
    onConfirm(sendEmail);
  };

  const canMail = !!plan?.mailType && !plan.skipReason;
  const loadingPlan = !plan && !planError;
  const offerMail = canMail || !!planError;
  const locked = busy || !armed || loadingPlan;

  return (
    <AlertDialog open={!!order} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <AlertDialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Status wijzigen naar {statusLabel}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-[#5a6552]">
              <p>
                Bestelling <strong className="text-[#1e2b22]">{order?.order_number}</strong> krijgt de status{' '}
                <strong className="text-[#1e2b22]">{statusLabel}</strong>.
              </p>
              <div className="rounded-lg border border-[#e7e1d6] bg-[#fcfaf6] p-3">
                {loadingPlan && (
                  <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Controleren welke klantmail hierbij hoort...</p>
                )}
                {planError && <p className="text-red-700">Kon niet controleren welke mail hierbij hoort: {planError}</p>}
                {plan && canMail && (
                  <p className="flex items-start gap-2 text-[#1e2b22]">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Klantmail: <strong>{MAIL_TYPE_LABELS[plan.mailType as string] ?? plan.mailType}</strong>
                      <br />
                      <span className="text-[#5a6552]">Naar {order?.customer_email}, met kopie naar info@buitnplanten.nl.</span>
                    </span>
                  </p>
                )}
                {plan && !canMail && (
                  <p className="flex items-start gap-2 text-amber-800">
                    <MailX className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{plan.skipReason ?? 'Voor deze status bestaat geen klantmail'}. Alleen de status wordt gewijzigd.</span>
                  </p>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:justify-start sm:space-x-0">
          {offerMail && (
            <Button className="h-auto w-full whitespace-normal py-2.5" onClick={() => choose(true)} disabled={locked}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" /> : <Mail className="mr-2 h-4 w-4 shrink-0" />}
              Status wijzigen en klant mailen
            </Button>
          )}
          <Button variant="outline" className="h-auto w-full whitespace-normal py-2.5" onClick={() => choose(false)} disabled={locked}>
            {offerMail ? 'Alleen status wijzigen (geen mail)' : 'Status wijzigen'}
          </Button>
          <Button variant="destructive" className="h-auto w-full whitespace-normal py-2.5" onClick={onCancel} disabled={busy}>
            Annuleren
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
